/**
 * LAMED COGITO 3D Knowledge Graph Visualization
 *
 * Real-time animated 3D visualization of LAMED's symbolic knowledge base
 * - Entities displayed as colored spheres (by type)
 * - Relations displayed as connecting lines
 * - Smooth animations for new knowledge
 * - Interactive camera controls (orbit, zoom, pan)
 */

import React, { useRef, useMemo, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text, Line } from '@react-three/drei';
import * as THREE from 'three';

// Entity type colors
const ENTITY_COLORS: Record<string, string> = {
  PERSON: '#00ffff',      // Cyan
  PLACE: '#00ff00',       // Green
  THING: '#ffff00',       // Yellow
  CONCEPT: '#ff00ff',     // Magenta
  ORGANIZATION: '#ff8800', // Orange
  EVENT: '#ff0088',       // Pink
  TIME: '#8800ff',        // Purple
};

// Particle system for Matrix-style background effect
function MatrixBackground() {
  const particlesRef = useRef<THREE.Points>(null);

  const particles = useMemo(() => {
    const count = 300;
    const positions = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 80;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 80;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 80;
    }

    return positions;
  }, []);

  useFrame((state) => {
    if (particlesRef.current) {
      particlesRef.current.rotation.y = state.clock.elapsedTime * 0.05;
    }
  });

  return (
    <points ref={particlesRef}>
      <bufferGeometry attach="geometry">
        <bufferAttribute
          attach="attributes-position"
          args={[particles, 3]}
        />
      </bufferGeometry>
      <pointsMaterial
        attach="material"
        size={0.1}
        color="#00ff00"
        transparent
        opacity={0.3}
        sizeAttenuation
      />
    </points>
  );
}

// Supernova particle system for new entities
interface SupernovaProps {
  position: [number, number, number];
  color: string;
}

function SupernovaEffect({ position, color }: SupernovaProps) {
  const particlesRef = useRef<THREE.Points>(null);
  const ringsRef = useRef<THREE.Group>(null);
  const [age, setAge] = React.useState(0);

  // Generate explosion particles
  const particlePositions = useMemo(() => {
    const count = 50;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      positions[i * 3] = Math.sin(phi) * Math.cos(theta) * 0.1;
      positions[i * 3 + 1] = Math.sin(phi) * Math.sin(theta) * 0.1;
      positions[i * 3 + 2] = Math.cos(phi) * 0.1;
    }
    return positions;
  }, []);

  useFrame((_state, delta) => {
    const newAge = age + delta;
    setAge(newAge);

    // Expand particles outward
    if (particlesRef.current && newAge < 2) {
      const expansion = newAge * 3;
      particlesRef.current.scale.setScalar(expansion);
      const material = particlesRef.current.material as THREE.PointsMaterial;
      material.opacity = Math.max(0, 1 - newAge / 2);
    }

    // Expand rings
    if (ringsRef.current && newAge < 1.5) {
      const ringScale = 1 + newAge * 2;
      ringsRef.current.scale.setScalar(ringScale);
      ringsRef.current.children.forEach((ring: any) => {
        ring.material.opacity = Math.max(0, 1 - newAge / 1.5);
      });
    }
  });

  if (age > 2) return null; // Animation finished

  return (
    <group position={position}>
      {/* Expanding rings */}
      <group ref={ringsRef}>
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.8, 1, 32]} />
          <meshBasicMaterial color={color} transparent opacity={0.8} side={THREE.DoubleSide} />
        </mesh>
        <mesh rotation={[0, 0, 0]}>
          <ringGeometry args={[0.8, 1, 32]} />
          <meshBasicMaterial color={color} transparent opacity={0.6} side={THREE.DoubleSide} />
        </mesh>
      </group>

      {/* Particle burst */}
      <points ref={particlesRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            args={[particlePositions, 3]}
          />
        </bufferGeometry>
        <pointsMaterial
          size={0.15}
          color={color}
          transparent
          opacity={1}
          sizeAttenuation
        />
      </points>
    </group>
  );
}

// Sparkling effect for updated entities
interface SparkleProps {
  position: [number, number, number];
  color: string;
}

function SparkleEffect({ position, color }: SparkleProps) {
  const sparklesRef = useRef<THREE.Points>(null);
  const [age, setAge] = React.useState(0);

  const sparklePositions = useMemo(() => {
    const count = 20;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const radius = 0.7 + Math.random() * 0.3;
      positions[i * 3] = Math.cos(angle) * radius;
      positions[i * 3 + 1] = Math.sin(angle) * radius;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 0.5;
    }
    return positions;
  }, []);

  useFrame((_state, delta) => {
    const newAge = age + delta;
    setAge(newAge);

    if (sparklesRef.current && newAge < 1.5) {
      // Rotate sparkles
      sparklesRef.current.rotation.y = newAge * 2;
      sparklesRef.current.rotation.z = newAge * 1.5;
      // Fade out
      const material = sparklesRef.current.material as THREE.PointsMaterial;
      material.opacity = Math.max(0, 1 - newAge / 1.5);
      // Pulse size
      const pulse = 1 + Math.sin(newAge * 8) * 0.3;
      material.size = 0.12 * pulse;
    }
  });

  if (age > 1.5) return null;

  return (
    <points ref={sparklesRef} position={position}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[sparklePositions, 3]}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.12}
        color={color}
        transparent
        opacity={1}
        sizeAttenuation
      />
    </points>
  );
}

// Entity node component
interface EntityNodeProps {
  entity: any;
  position: [number, number, number];
  onHover?: (entity: any | null) => void;
}

function EntityNode({ entity, position, onHover }: EntityNodeProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = React.useState(false);
  const [scale, setScale] = React.useState(0);
  const [showSupernova, setShowSupernova] = React.useState(false);
  const [showSparkle, setShowSparkle] = React.useState(false);

  // Detect change_type and trigger animations
  useEffect(() => {
    if (entity.change_type === 'new') {
      setShowSupernova(true);
      setTimeout(() => setShowSupernova(false), 2000);
    } else if (entity.change_type === 'updated') {
      setShowSparkle(true);
      setTimeout(() => setShowSparkle(false), 1500);
    }
  }, [entity.change_type, entity.id]);

  // Animate entity appearing
  useEffect(() => {
    const timer = setTimeout(() => setScale(1), 100);
    return () => clearTimeout(timer);
  }, []);

  // Pulse animation
  useFrame((state) => {
    if (meshRef.current) {
      const pulse = 1 + Math.sin(state.clock.elapsedTime * 2) * 0.05;
      meshRef.current.scale.setScalar(scale * pulse * (hovered ? 1.3 : 1));
    }
  });

  const color = ENTITY_COLORS[entity.type] || '#ffffff';

  return (
    <group position={position}>
      <mesh
        ref={meshRef}
        onPointerOver={() => {
          setHovered(true);
          onHover?.(entity);
        }}
        onPointerOut={() => {
          setHovered(false);
          onHover?.(null);
        }}
      >
        <sphereGeometry args={[0.5, 32, 32]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={hovered ? 0.8 : 0.3}
          transparent
          opacity={0.9}
        />
      </mesh>

      {/* Compact entity label - only show when hovered or name is short */}
      {(hovered || entity.name.length <= 12) && (
        <Text
          position={[0, 0.7, 0]}
          fontSize={0.2}
          color={color}
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.015}
          outlineColor="#000000"
          maxWidth={2}
          overflowWrap="break-word"
        >
          {entity.name.length > 15 ? entity.name.substring(0, 12) + '...' : entity.name}
        </Text>
      )}

      {/* Type indicator - tiny text below */}
      {hovered && (
        <Text
          position={[0, -0.7, 0]}
          fontSize={0.12}
          color="#666666"
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.008}
          outlineColor="#000000"
        >
          {entity.type}
        </Text>
      )}

      {/* Supernova animation for new entities */}
      {showSupernova && <SupernovaEffect position={[0, 0, 0]} color={color} />}

      {/* Sparkling animation for updated entities */}
      {showSparkle && <SparkleEffect position={[0, 0, 0]} color={color} />}
    </group>
  );
}

// Relation edge component with bezier curves
interface RelationEdgeProps {
  start: [number, number, number];
  end: [number, number, number];
  relation: any;
}

function RelationEdge({ start, end, relation }: RelationEdgeProps) {
  // Color based on confidence
  const color = relation.confidence > 0.7 ? '#00ff00' : '#ffff00';

  // Create bezier curve for smooth, organic connections
  const points = useMemo(() => {
    const startVec = new THREE.Vector3(...start);
    const endVec = new THREE.Vector3(...end);

    // Calculate control point for bezier curve
    // Place it perpendicular to the line, creating an arc
    const midpoint = new THREE.Vector3().lerpVectors(startVec, endVec, 0.5);
    const direction = new THREE.Vector3().subVectors(endVec, startVec);
    const distance = direction.length();

    // Create perpendicular vector for curve offset
    const perpendicular = new THREE.Vector3(-direction.y, direction.x, direction.z * 0.5).normalize();
    const offset = perpendicular.multiplyScalar(distance * 0.15); // Curve intensity

    const controlPoint = midpoint.clone().add(offset);

    // Generate smooth bezier curve points
    const curve = new THREE.QuadraticBezierCurve3(startVec, controlPoint, endVec);
    return curve.getPoints(20); // 20 segments for smooth curve
  }, [start, end]);

  return (
    <Line
      points={points}
      color={color}
      lineWidth={1.5}
      transparent={true}
      opacity={0.5}
    />
  );
}

// Main graph scene component
interface GraphSceneProps {
  knowledge: any;
  onHoverEntity?: (entity: any | null) => void;
}

function GraphScene({ knowledge, onHoverEntity }: GraphSceneProps) {
  // Persistent position cache (survives across updates)
  const positionCacheRef = useRef<Map<string, [number, number, number]>>(new Map());

  // Calculate entity positions in 3D space using force-directed layout
  // STABLE POSITIONS: Reuses cached positions for known entities
  const entityPositions = useMemo(() => {
    if (!knowledge?.entities || knowledge.entities.length === 0) {
      return new Map();
    }

    const positions = new Map<string, [number, number, number]>();

    // Initialize positions: use cached positions for known entities, random for new ones
    knowledge.entities.forEach((entity: any) => {
      if (positionCacheRef.current.has(entity.id)) {
        // STABLE: Reuse existing position for known entity
        positions.set(entity.id, positionCacheRef.current.get(entity.id)!);
      } else {
        // NEW: Random position for new entity
        const newPos: [number, number, number] = [
          (Math.random() - 0.5) * 20,
          (Math.random() - 0.5) * 20,
          (Math.random() - 0.5) * 20
        ];
        positions.set(entity.id, newPos);
      }
    });

    // Build adjacency map from relations
    const adjacency = new Map<string, Set<string>>();
    knowledge.entities.forEach((entity: any) => {
      adjacency.set(entity.id, new Set());
    });

    (knowledge.relations || []).forEach((rel: any) => {
      if (adjacency.has(rel.subject) && adjacency.has(rel.object)) {
        adjacency.get(rel.subject)!.add(rel.object);
        adjacency.get(rel.object)!.add(rel.subject);
      }
    });

    // Force-directed layout simulation (50 iterations for stable layout)
    for (let iteration = 0; iteration < 50; iteration++) {
      const forces = new Map<string, THREE.Vector3>();

      // Initialize forces
      knowledge.entities.forEach((entity: any) => {
        forces.set(entity.id, new THREE.Vector3(0, 0, 0));
      });

      // Repulsion: All nodes repel each other (prevents overlap)
      knowledge.entities.forEach((entity1: any) => {
        knowledge.entities.forEach((entity2: any) => {
          if (entity1.id === entity2.id) return;

          const pos1 = new THREE.Vector3(...positions.get(entity1.id)!);
          const pos2 = new THREE.Vector3(...positions.get(entity2.id)!);
          const delta = new THREE.Vector3().subVectors(pos1, pos2);
          const distance = Math.max(delta.length(), 0.1); // Prevent division by zero

          // Stronger repulsion at close range (inverse square law)
          const repulsionForce = 15 / (distance * distance);
          delta.normalize().multiplyScalar(repulsionForce);

          forces.get(entity1.id)!.add(delta);
        });
      });

      // Attraction: Connected nodes attract each other (keeps related things close)
      (knowledge.relations || []).forEach((rel: any) => {
        const pos1 = positions.get(rel.subject);
        const pos2 = positions.get(rel.object);
        if (!pos1 || !pos2) return;

        const p1 = new THREE.Vector3(...pos1);
        const p2 = new THREE.Vector3(...pos2);
        const delta = new THREE.Vector3().subVectors(p2, p1);
        const distance = delta.length();

        // Spring-like attraction (stronger when far apart)
        const attractionForce = distance * 0.1;
        delta.normalize().multiplyScalar(attractionForce);

        forces.get(rel.subject)!.add(delta);
        forces.get(rel.object)!.sub(delta);
      });

      // Apply forces with damping
      const damping = 0.5;
      knowledge.entities.forEach((entity: any) => {
        const pos = positions.get(entity.id)!;
        const force = forces.get(entity.id)!;

        pos[0] += force.x * damping;
        pos[1] += force.y * damping;
        pos[2] += force.z * damping;

        // Keep nodes within bounds
        const maxDist = 25;
        const distFromCenter = Math.sqrt(pos[0]**2 + pos[1]**2 + pos[2]**2);
        if (distFromCenter > maxDist) {
          const scale = maxDist / distFromCenter;
          pos[0] *= scale;
          pos[1] *= scale;
          pos[2] *= scale;
        }
      });
    }

    // Update position cache with final positions
    positions.forEach((pos, entityId) => {
      positionCacheRef.current.set(entityId, pos);
    });

    return positions;
  }, [knowledge?.entities, knowledge?.relations]);

  return (
    <>
      {/* Ambient lighting */}
      <ambientLight intensity={0.3} />

      {/* Directional light */}
      <directionalLight position={[10, 10, 5]} intensity={0.5} />

      {/* Point light following camera */}
      <pointLight position={[0, 0, 0]} intensity={0.5} color="#00ff00" />

      {/* Matrix background particles */}
      <MatrixBackground />

      {/* Entity nodes */}
      {knowledge?.entities?.map((entity: any) => {
        const position = entityPositions.get(entity.id);
        if (!position) return null;

        return (
          <EntityNode
            key={entity.id}
            entity={entity}
            position={position}
            onHover={onHoverEntity}
          />
        );
      })}

      {/* Relation edges */}
      {knowledge?.relations?.map((relation: any, index: number) => {
        const startPos = entityPositions.get(relation.subject);
        const endPos = entityPositions.get(relation.object);

        if (!startPos || !endPos) return null;

        return (
          <RelationEdge
            key={`${relation.subject}-${relation.predicate}-${relation.object}-${index}`}
            start={startPos}
            end={endPos}
            relation={relation}
          />
        );
      })}

      {/* Orbit controls */}
      <OrbitControls
        enablePan={true}
        enableZoom={true}
        enableRotate={true}
        minDistance={5}
        maxDistance={50}
        autoRotate={knowledge?.entities?.length > 0}
        autoRotateSpeed={0.5}
      />
    </>
  );
}

// Main component
interface KnowledgeGraph3DProps {
  knowledge: any;
}

export default function KnowledgeGraph3D({ knowledge }: KnowledgeGraph3DProps) {
  const [hoveredEntity, setHoveredEntity] = React.useState<any | null>(null);

  const hasData = knowledge?.entities && knowledge.entities.length > 0;

  return (
    <div className="relative w-full h-full bg-black">
      {/* Canvas for 3D rendering */}
      <Canvas
        camera={{ position: [0, 0, 20], fov: 60 }}
        style={{ background: '#000000' }}
      >
        <GraphScene knowledge={knowledge} onHoverEntity={setHoveredEntity} />
      </Canvas>

      {/* HUD Overlay */}
      <div className="absolute top-0 left-0 right-0 p-4 pointer-events-none">
        <div className="bg-black/80 backdrop-blur-sm border border-green-500/30 rounded-lg p-3">
          <div className="text-green-400 text-xs font-mono mb-2">
            <span className="text-green-300 font-bold">[COGITO KNOWLEDGE GRAPH]</span>
          </div>

          <div className="grid grid-cols-3 gap-4 text-[10px]">
            <div>
              <div className="text-green-500/60">ENTITIES</div>
              <div className="text-green-300 font-bold">
                {knowledge?.entities?.length || 0}
              </div>
            </div>
            <div>
              <div className="text-green-500/60">RELATIONS</div>
              <div className="text-green-300 font-bold">
                {knowledge?.relations?.length || 0}
              </div>
            </div>
            <div>
              <div className="text-green-500/60">FACTS</div>
              <div className="text-green-300 font-bold">
                {knowledge?.facts?.length || 0}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hovered entity info */}
      {hoveredEntity && (
        <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
          <div className="bg-black/90 backdrop-blur-sm border border-cyan-500/50 rounded-lg p-3">
            <div className="text-cyan-300 text-sm font-mono font-bold mb-1">
              {hoveredEntity.name}
            </div>
            <div className="text-cyan-400/80 text-xs mb-2">
              Type: {hoveredEntity.type}
            </div>
            {hoveredEntity.attributes && Object.keys(hoveredEntity.attributes).length > 0 && (
              <div className="text-cyan-400/60 text-[10px]">
                {Object.entries(hoveredEntity.attributes).map(([k, v]) => (
                  <div key={k}>• {k}: {String(v)}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* No data message */}
      {!hasData && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center text-green-500/50">
            <div className="text-6xl mb-4">🧠</div>
            <div className="text-sm font-mono mb-2">KNOWLEDGE GRAPH INITIALIZING</div>
            <div className="text-xs opacity-60">Start a conversation to build the graph...</div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-4 right-4 pointer-events-none">
        <div className="bg-black/80 backdrop-blur-sm border border-green-500/30 rounded-lg p-3">
          <div className="text-green-400 text-[10px] font-mono font-bold mb-2">LEGEND</div>
          <div className="space-y-1 text-[9px]">
            {Object.entries(ENTITY_COLORS).map(([type, color]) => (
              <div key={type} className="flex items-center gap-2">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: color, boxShadow: `0 0 4px ${color}` }}
                />
                <span style={{ color }}>{type}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Controls hint */}
      <div className="absolute top-4 right-4 pointer-events-none">
        <div className="bg-black/60 backdrop-blur-sm border border-green-500/20 rounded px-2 py-1">
          <div className="text-green-500/60 text-[9px] font-mono">
            🖱️ DRAG • 🎯 ZOOM • 🔄 AUTO-ROTATE
          </div>
        </div>
      </div>
    </div>
  );
}
